import React, { useState } from 'react';
import { Route, Switch, useParams } from 'react-router';
import useDeepCompareEffect from 'use-deep-compare-effect';

import useProjectTags from '@API/project/useProjectTags';
import { DashboardRouteParams } from '@experiment-management-shared/types';
import EMDashboardHeader from '@projects/components/EMDashboardHeader';
import ExperimentActions from '@projects/components/ExperimentActions';
import ExperimentsPage from '@projects/components/ExperimentsPage';
import PanelsPage from '@projects/components/PanelsPage';
import EMProjectPageHeader from '@shared/components/EMProjectPageHeader';
import ProjectPageWrapper from '@shared/components/ProjectPageWrapper';
import EMDashboardLoader from './EMDashboardLoader';
import {
  useDashboardActiveExperimentTab,
  useDashboardExperimentsData,
  useDashboardRefetchInterval,
  useDashboardSelectedView,
  useDashboardTabs,
  useEMDashboardExport,
  useEMDashboardGroups,
  useEMDashboardRedirect,
  useIsArchiveSection,
  useNonExistedExperimentMsg,
  useProjectViewedBI,
  useSetDefaultEMHeaderTab,
  useSyncExperimentsWithView
} from './hooks';
import { useDashboardApiContext } from './useDashboardAPIContext';

const EMDashboardPageContent = () => {
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  const { viewId } = useParams<DashboardRouteParams>();
  const isArchiveSection = useIsArchiveSection();
  const { onSave, exportRef, exportHandler } = useEMDashboardExport();
  // core part of the dashboard page, it selects the correct view
  // it auto-saves (flush pending requests) when needed
  // it exposes an optimized `updateDashboardView` to update the view from outside
  // (only method to update, do not expose `setDashboardView`, expose other methods
  // if needed like `resetDashboardView`)
  const {
    dashboardView,
    recalculateAutoGeneratedPanels,
    resetDashboardView,
    updateDashboardView
  } = useDashboardSelectedView();
  const refetchInterval = useDashboardRefetchInterval({
    isAutoRefreshEnabled: Boolean(dashboardView?.panels?.isAutoRefreshEnabled)
  });
  const { extraColumns: availableColumns } = useDashboardApiContext();

  // prefetch tags
  useProjectTags({}, { notifyOnChangeProps: [], refetchInterval });

  const {
    activeExperimentsHeaderTab,
    experimentKeysForExperimentsTab
  } = useDashboardActiveExperimentTab({
    hiddenExperimentKeys: dashboardView?.hiddenExperimentKeys,
    selection: dashboardView?.table?.selection,
    updateDashboardView
  });

  const {
    groupsQuery,
    groups,
    groupAggregations,
    experimentsGroupsMap,
    isFetchingExperimentGroups,
    totalGroups
  } = useEMDashboardGroups({
    dashboardView,
    refetchInterval,
    isArchiveSection,
    updateDashboardView
  });

  const {
    tableExperiments,
    selectedExperiments,
    totalExperiments,
    panelExperiments,
    isFetchingAnyExperiments,
    isPreviousDataAnyExperiments,
    isLoadingAnyExperiments,
    isExperimentsDataReady,
    visibleExperimentKeys
  } = useDashboardExperimentsData({
    isArchiveSection,
    experimentKeysForExperimentsTab,
    refetchInterval,
    // @todo: fix type, after `useDeepMemo` was fixed the real type popup this issue
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    groupsQuery,
    dashboardView,
    viewId,
    updateDashboardView
  });

  const isLoadingData = !isExperimentsDataReady || dashboardView === null;

  // scroll to top on change keys
  // panels could be changed and infinite scroll needs to reinitialize
  useDeepCompareEffect(() => {
    if (isLoadingAnyExperiments) return;

    recalculateAutoGeneratedPanels(visibleExperimentKeys);
    // window.scrollTo(0, 0);
  }, [isLoadingAnyExperiments, visibleExperimentKeys]);

  // a non-existent experiment will redirect to here and we have to display a message
  useNonExistedExperimentMsg();

  // restores the selection of experiments before the user left the page (redux)
  useSyncExperimentsWithView({
    dashboardView,
    updateDashboardView
  });

  // restores experiments selection on change tabs
  // returns current section (from params or /:workspace/:projectName/:section)
  const section = useDashboardTabs({
    updateDashboardView
  });

  // redirect to the correct final structure of a view in case some parts are missing
  // (/:workspaceName/:projectName/view/:viewId/:section)
  useEMDashboardRedirect({ section });

  // sync active header tab with redux (used to check `isArchive` in other places)
  useSetDefaultEMHeaderTab({ section });
  // send PROJECT_VIEWED BI event
  useProjectViewedBI();

  if (isLoadingData) {
    return <EMDashboardLoader />;
  }

  const pageProps = {
    availableColumns,
    activeExperimentsHeaderTab,
    dashboard: dashboardView,
    displayGroups: groups,
    experimentsGroupsMap,
    groupAggregations,
    experiments: tableExperiments,
    isArchive: isArchiveSection,
    isFetchingExperiments: isFetchingAnyExperiments,
    isLoadingExperiments: isLoadingAnyExperiments,
    isFetchingExperimentGroups,
    isPreviousDataExperiments: isPreviousDataAnyExperiments,
    onChangeDashboard: updateDashboardView,
    onChangeHeaderVisibility: setIsHeaderSticky,
    onSave,
    pageTotalGrouped: totalGroups,
    ref: exportRef,
    selectedExperiments,
    totalExperiments
  };

  return (
    <ProjectPageWrapper
      header={
        <EMProjectPageHeader
          subheaderRightSection={
            <EMDashboardHeader
              dashboard={dashboardView}
              onChange={updateDashboardView}
              resetDashboardView={resetDashboardView}
            />
          }
          sticky={isHeaderSticky}
        />
      }
      content={
        <>
          <Switch>
            <Route
              path="/:workspace/:projectName/view/:viewId/archive"
              exact
              render={() => <ExperimentsPage {...pageProps} isArchive />}
            />
            <Route
              path="/:workspace/:projectName/view/:viewId/experiments"
              exact
              render={() => <ExperimentsPage {...pageProps} />}
            />
            <Route
              path="/:workspace/:projectName/view/:viewId/panels"
              exact
              render={() => (
                <PanelsPage
                  loadingPanels={false}
                  panelExperiments={panelExperiments}
                  {...pageProps}
                />
              )}
            />
          </Switch>

          {selectedExperiments.length > 0 && (
            <ExperimentActions
              dashboard={dashboardView}
              hiddenExperimentKeys={dashboardView.hiddenExperimentKeys}
              onChange={updateDashboardView}
              exportHandler={exportHandler}
              selectedExperiments={selectedExperiments}
              totalExperiments={totalExperiments}
              experiments={tableExperiments}
            />
          )}
        </>
      }
    />
  );
};

export default EMDashboardPageContent;
